import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MarketingNav from "@/components/site/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import PasswordStrength, { scorePassword } from "@/components/site/PasswordStrength";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const { tier } = scorePassword(password);
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = token && password.length >= 8 && !mismatch && tier >= 1 && !busy;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — sign in with your new password.");
      setTimeout(() => navigate("/login"), 1600);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
      setError(String(msg));
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-background">
        <MarketingNav />
        <div className="max-w-md mx-auto px-6 md:px-8 py-24" data-testid="reset-missing-token">
          <div className="overline text-destructive">Invalid link</div>
          <h1 className="font-serif-display text-4xl mt-3 leading-tight">This reset link is missing a token.</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Ask for a fresh link from the <Link to="/forgot" className="text-primary underline">forgot password</Link> page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="reset-password-page">
      <MarketingNav />
      <div className="max-w-md mx-auto px-6 md:px-8 py-16 md:py-24">
        <Link to="/login" className="inline-flex items-center gap-2 overline text-muted-foreground hover:text-primary">
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </Link>
        <div className="overline text-muted-foreground mt-8">Set a new password</div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-none tracking-tight mt-3">
          New key.<br /><span className="italic text-primary">Same account.</span>
        </h1>

        {done ? (
          <div className="mt-8 border border-border p-6 bg-background/60 backdrop-blur-sm" data-testid="reset-done">
            <div className="overline text-primary">Success</div>
            <h2 className="font-serif-display text-2xl mt-2">You're all set.</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Taking you back to sign in…
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            <div>
              <label className="overline text-muted-foreground">New password</label>
              <div className="relative mt-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  placeholder="At least 8 characters"
                  className="rounded-none h-11 font-mono text-sm pr-11"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  data-testid="reset-toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <PasswordStrength password={password} />
            </div>
            <div>
              <label className="overline text-muted-foreground">Confirm password</label>
              <Input
                type={showPassword ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="Retype it"
                className="rounded-none mt-2 h-11 font-mono text-sm"
                data-testid="reset-confirm-input"
              />
              {mismatch && (
                <p className="mt-1 text-xs text-destructive" data-testid="reset-mismatch">Passwords don't match.</p>
              )}
            </div>
            {error && (
              <div className="text-sm text-destructive font-mono" data-testid="reset-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={!canSubmit}
              className="rounded-none h-12 px-8 font-mono uppercase tracking-widest text-xs w-full md:w-auto"
              data-testid="reset-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
